import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { getCardCoverUrl, CARD_PLACEHOLDER_SRC } from '../lib/cardImage';

/**
 * The one card-cover image renderer for the whole store (browse, cart, personalize) — kills the
 * per-page size drift that produced webp-here / thumb-there / a broken `.jpg` fallback.
 *
 * Cascade: WebP `preview`  →(on error) source `full`  →(on error) a dignified placeholder asset.
 * - preview-first gives WebP everywhere;
 * - the `full` step self-heals dev, where `preview.webp` isn't generated locally;
 * - the placeholder covers a card with no image / any failed load — never a broken-image icon.
 * When there is no cover image id at all, the placeholder renders directly (no broken request).
 *
 * `coverImageUrl` (the card-list's inline, directly-fetchable WebP preview URL — CDN or presigned)
 * is preferred as the primary `src` when present: it skips the per-card `/preview` API hop (and its
 * 302) entirely, and lets Phase-2 swap a CDN URL in with no change here. It still falls back to the
 * source `full` derivative, then the placeholder.
 *
 * `forcedSize` pins a single size (e.g. a full-res zoom): that size → placeholder, no preview→full hop.
 */
const CardCoverImage = ({ cardId, imageId, coverImageUrl, alt = '', forcedSize, sx, ...rest }) => {
  const hasImage = Boolean(coverImageUrl || (cardId && imageId));
  // 'primary' = coverImageUrl || forcedSize || preview; 'full' = source fallback; 'placeholder' = give up.
  const [stage, setStage] = useState('primary');

  // Reset the cascade whenever the target image changes (e.g. a rail patches in its resolved cover).
  useEffect(() => {
    setStage('primary');
  }, [cardId, imageId, coverImageUrl, forcedSize]);

  let src;
  if (!hasImage || stage === 'placeholder') {
    src = CARD_PLACEHOLDER_SRC;
  } else if (stage === 'full') {
    // Source-format fallback. Needs cardId+imageId; if we only had a direct URL, give up gracefully.
    src = getCardCoverUrl(cardId, imageId, 'full') || CARD_PLACEHOLDER_SRC;
  } else if (coverImageUrl && !forcedSize) {
    src = coverImageUrl;
  } else {
    src = getCardCoverUrl(cardId, imageId, forcedSize || 'preview');
  }

  const handleError = () => {
    setStage((prev) => {
      if (prev === 'primary' && !forcedSize) return 'full'; // preview → full
      return 'placeholder'; // full → placeholder, or a pinned size → placeholder
    });
  };

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      data-testid="card-cover-image"
      // Once we're showing the placeholder there's nothing left to fall back to.
      onError={src === CARD_PLACEHOLDER_SRC ? undefined : handleError}
      sx={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain', ...sx }}
      {...rest}
    />
  );
};

export default CardCoverImage;
